import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Checkbox,
	Grid2,
	InputAdornment,
	LinearProgress,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useAnimatedRowReorder } from "../../../../hooks/useAnimatedRowReorder";
import type { EmailTopic } from "../../emailTopics/models/EmailTopic";

export type TopicField = "TopicIdsTO" | "TopicIdsCC" | "TopicIdsBCC";

export const TOPIC_FIELDS: { label: string; field: TopicField }[] = [
	{ label: "TO", field: "TopicIdsTO" },
	{ label: "CC", field: "TopicIdsCC" },
	{ label: "BCC", field: "TopicIdsBCC" },
];

type Props = {
	topics: EmailTopic[];
	isLoading: boolean;
	error?: Error | null;
	selectedByField: Record<TopicField, number[]>;
	onToggle: (field: TopicField, emailTopicId: number, checked: boolean) => void;
	onError: (message: string) => void;
	disabled?: boolean;
};

export const AssignedTopicsTable = ({
	topics,
	isLoading,
	error,
	selectedByField,
	onToggle,
	onError,
	disabled = false,
}: Props) => {
	const [topicFilter, setTopicFilter] = useState("");

	const selectedByFieldRef = useRef(selectedByField);
	selectedByFieldRef.current = selectedByField;

	const { containerRef, setRowRef, captureLayout } =
		useAnimatedRowReorder<number>();

	const getVisibleTopics = () => {
		const isTopicSelected = (emailTopicId: number) =>
			TOPIC_FIELDS.some((recipient) =>
				selectedByFieldRef.current[recipient.field].includes(emailTopicId),
			);

		const needle = topicFilter.trim().toLowerCase();
		const filtered = needle
			? topics.filter((topic) =>
					topic.EmailTopicName.toLowerCase().includes(needle),
				)
			: topics;

		return [...filtered].sort((a, b) => {
			const byChecked =
				Number(isTopicSelected(b.EmailTopicId)) -
				Number(isTopicSelected(a.EmailTopicId));

			return byChecked !== 0
				? byChecked
				: a.EmailTopicName.localeCompare(b.EmailTopicName);
		});
	};

	const visibleTopics = getVisibleTopics();

	const selectRecipientType = (
		field: TopicField,
		emailTopicId: number,
		checked: boolean,
	) => {
		captureLayout();

		if (checked) {
			for (const recipient of TOPIC_FIELDS) {
				if (
					recipient.field !== field &&
					selectedByField[recipient.field].includes(emailTopicId)
				) {
					onToggle(recipient.field, emailTopicId, false);
				}
			}
		}

		onToggle(field, emailTopicId, checked);
	};

	const toggleAllVisible = (field: TopicField, checked: boolean) => {
		captureLayout();

		const selected = selectedByField[field];

		for (const topic of visibleTopics) {
			const isInField = selected.includes(topic.EmailTopicId);

			if (checked && !isInField) {
				selectRecipientType(field, topic.EmailTopicId, true);
			} else if (!checked && isInField) {
				onToggle(field, topic.EmailTopicId, false);
			}
		}
	};

	useEffect(() => {
		if (error) {
			onError(error.message);
		}
	}, [error]);

	return (
		<>
			<Typography variant="subtitle2" fontWeight={600}>
				Topics
			</Typography>

			<TextField
				fullWidth
				label="Search topic"
				variant="outlined"
				size="small"
				margin="dense"
				value={topicFilter}
				disabled={disabled}
				onChange={(event) => setTopicFilter(event.target.value)}
				sx={{ mb: 1 }}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					} as any,
				}}
			/>

			{isLoading ? <LinearProgress /> : null}

			<Box
				sx={{
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					overflow: "hidden",
				}}
			>
				<Box ref={containerRef} sx={{ maxHeight: "30vh", overflowY: "auto" }}>
					<Grid2
						container
						alignItems="center"
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 2,
							backgroundColor: "background.paper",
							borderBottom: 1,
							borderColor: "divider",
							px: 1,
							py: 0.5,
						}}
					>
						<Grid2 size={{ xs: 6 }}>
							<Typography variant="caption" fontWeight={600}>
								Topic
							</Typography>
						</Grid2>
						{TOPIC_FIELDS.map((recipient) => {
							const selected = selectedByField[recipient.field];
							const selectedVisible = visibleTopics.filter((topic) =>
								selected.includes(topic.EmailTopicId),
							).length;
							const allSelected =
								visibleTopics.length > 0 &&
								selectedVisible === visibleTopics.length;

							return (
								<Grid2
									key={recipient.field}
									size={{ xs: 2 }}
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<Typography variant="caption" fontWeight={600}>
										{recipient.label}
									</Typography>
									<Checkbox
										size="small"
										color="primary"
										sx={{ p: 0.25 }}
										title={`Select all ${recipient.label}`}
										disabled={disabled || visibleTopics.length === 0}
										checked={allSelected}
										indeterminate={selectedVisible > 0 && !allSelected}
										onChange={(event) =>
											toggleAllVisible(recipient.field, event.target.checked)
										}
									/>
								</Grid2>
							);
						})}
					</Grid2>

					{visibleTopics.map((topic) => (
						<Grid2
							container
							alignItems="center"
							key={topic.EmailTopicId}
							ref={setRowRef(topic.EmailTopicId)}
							sx={{
								px: 1,
								minHeight: 38,
								backgroundColor: "background.paper",
								"&:hover": { backgroundColor: "action.hover" },
							}}
						>
							<Grid2
								size={{ xs: 6 }}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: "0.25rem",
								}}
							>
								{topic.IsSystem && (
									<Tooltip
										title={
											<>
												You can see this topic because your username is{" "}
												<b>admin</b> or <b>dexteel</b>.
											</>
										}
										slotProps={{
											tooltip: { sx: { fontSize: "0.75rem", fontWeight: 400 } },
										}}
									>
										<LockIcon sx={{ fontSize: "0.9rem" }} color="action" />
									</Tooltip>
								)}
								<Typography variant="body2" noWrap title={topic.EmailTopicName}>
									{topic.EmailTopicName}
								</Typography>
							</Grid2>
							{TOPIC_FIELDS.map((recipient) => (
								<Grid2
									key={recipient.field}
									size={{ xs: 2 }}
									sx={{ display: "flex", justifyContent: "center" }}
								>
									<Checkbox
										size="small"
										color="primary"
										disabled={disabled}
										checked={selectedByField[recipient.field].includes(
											topic.EmailTopicId,
										)}
										onChange={(event) =>
											selectRecipientType(
												recipient.field,
												topic.EmailTopicId,
												event.target.checked,
											)
										}
									/>
								</Grid2>
							))}
						</Grid2>
					))}

					{!isLoading && visibleTopics.length === 0 ? (
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{ px: 1, py: 2, textAlign: "center" }}
						>
							{topicFilter.trim()
								? "No topics match the search."
								: "No topics configured."}
						</Typography>
					) : null}
				</Box>
			</Box>
		</>
	);
};
